import { useTranslation } from "react-i18next"
import WhiteSpace from "../../components/Whitespace"
import PageTitle from "../../components/PageTitle"
import ChargerType from "../../types/ChargerType"
import styled, { css } from "styled-components"
import { useEffect, useState } from "react"
import ChargeType from "../../types/ChargeType"
import Comm from "../../Comm"
import LoaderDots from "../../components/LoaderDots"
import Navigator from "../../components/Navigator"
import SlideToConfirm from "../../components/SlideToConfirm"
import dateToTimeAgoString from "../../utils/dateToTimeAgoString"
import { child, get, getDatabase, ref, set } from "firebase/database"
import fbConf from "../../FirebaseConf"
import { getAuth } from "firebase/auth"
import ButtonWithIcon from "../../components/ButtonWithIcon"
import Modal from "../../components/Modal"
import Button from "../../components/Button"
import Icon from "../../Icon"
import React from "react"
import secondsToHumanReadable from "../../utils/secondsToHumanReadable"
import Switch from "../../components/Switch"
import InputWithLabel from "../../components/InputWithLabel"
import Select from "../../components/Select"
import { useConfirm } from "../../ConfirmProvider"
import { useNotification } from "../../modules/Notification"
import { BREAKPOINTS, TAP_MIN } from "../../GlobalTheme"
import DailyMeterReadings from "./DailyMeterReadings"
import { currencyOptions, defaultCurrencyForLanguage, formatPrice, normalizeCurrency, parsePrice, priceInputText } from "../../utils/currency"

interface EvseStateType {
  sys_time: number,
  evse_state: number,
  phase: 1 | 3,
  u_eff: number,
  chg_on: boolean,
  chg_current: number,
  chg_power: number,
  srs: number,
  chg_enabled: boolean,
  autostart: number,
  temp_uc: number,
  temp_pow: number,
  temp_pi: number,
  hum: number,
  first_cdr: number,
  last_cdr: number,
  max_current: number,
}

const HeaderRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 3%;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-direction: column;
    gap: 12px;
  }
`

const GeneralContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: 18px;
  border-radius: 24px;
  position: relative;
  flex: 1 1 0;
  min-width: 0;
`

const GeneralCardRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 36px;

  /* A 160px status square plus a 36px gap leaves ~130px for the fields on a phone. Stack instead. */
  @media (max-width: ${BREAKPOINTS.phone}px) {
    flex-direction: column;
    gap: 16px;
  }
`

const GeneralCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 0;
  min-width: 0;
`

const IdentityRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 32px;
`

const IdentityBlock = styled.div`
  min-width: 0;
`

const GeneralCardSpacer = styled.div`
  flex: 1 1 auto;
  min-height: 12px;

  @media (max-width: ${BREAKPOINTS.phone}px) {
    display: none;
  }
`

const StatusContainer = styled.div<{ $bgColorTop: string, $bgColorBottom: string }>`
  position: relative;
  flex: 0 0 auto;
  width: max(12.5vw, 160px);
  height: max(12.5vw, 160px);
  background-image: ${props => `linear-gradient(${props.$bgColorTop}, ${props.$bgColorBottom})`};
  border-radius: 16px;
  user-select: none;
  -webkit-user-select: none;
`

const StatusTitle = styled.span<{ $bgColor: string, $fontColor: string }>`
  position: absolute;
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.$fontColor};
  background-color: ${props => props.$bgColor};
  border: 1px solid white;
  border-radius: 40px;
  padding-left: 15px;
  padding-right: 15px;
  padding-top: 5px;
  padding-bottom: 5px;
  transform: translate(-50%, -50%);
  left: 50%;
`

const StatusOfflineLabel = styled.p`
  font-weight: 500;
  font-size: 12px;
  color: #718096;
  width: 100%;
  margin: 0px;
  padding-top: 20px;
  text-align: center;
`

const StatusLabel = styled.p<{ $color: string }>`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.$color};
  margin: 0px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
`

/* The action icons sit in a bare <a> used only to carry a tooltip. Each icon already grows its own
   tap area to 44px, but at a 10px spacing those areas overlap, so a tap near an edge fires the
   neighbour. Giving every <a> a full tap box lays them out exactly one tap width apart. */
const tappableLabelIcons = css`
  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    > a {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: ${TAP_MIN}px;
      min-height: ${TAP_MIN}px;
    }

    /* The spacing lives in inline styles on the icons themselves, which nothing else can release. */
    > a svg {
      margin-left: 0px !important;
      margin-right: 0px !important;
    }
  }
`

const ChargerNameLabel = styled.p`
  font-weight: 700;
  font-size: 18px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* IE10+/Edge */
  user-select: none; /* Standard */
  ${tappableLabelIcons}
`

const ChargerNameValue = styled.p`
  font-weight: 700;
  font-size: 28px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-top: 4px;
  cursor: pointer;
  overflow-wrap: anywhere;
`

const UserIdEmailLabel = styled.p`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
  ${tappableLabelIcons}
`

const UserIdEmailValue = styled.p`
  font-weight: 600;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-top: 4px;
  overflow-wrap: anywhere;
`

const GeneralDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px 24px;

  > div {
    min-width: 0;
  }
`

const GeneralDetailsDivider = styled.div`
  width: 100%;
  height: 2px;
  background-image: linear-gradient(270deg, #E2594B 0%, #2A6FF4 100%);
`

const GeneralDetailLabel = styled.p`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
`

const GeneralDetailValue = styled.p`
  font-weight: 600;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  overflow-wrap: anywhere;
`

const ConnectionContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: 18px;
  border-radius: 24px;
  position: relative;

  /* The tables inside are flex rows of three fixed-share cells. Those rows carry an inline
     display:flex, so letting them wrap has to be done from here. */
  @media (max-width: ${BREAKPOINTS.phone}px) {
    tr {
      flex-wrap: wrap;
    }
  }
`

/* Was an inline-styled span 18px tall - unusable as a touch target. Same look, real tap height. */
const ConnectionTab = styled.span<{ $selected: boolean }>`
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
  cursor: pointer;
  background-color: ${props => (props.$selected ? '#4AB1FC' : '#F5F8FA')};
  color: ${props => (props.$selected ? '#fff' : '#A1A5B7')};

  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: inline-flex;
    align-items: center;
    min-height: ${TAP_MIN}px;
    min-width: ${TAP_MIN}px;
    justify-content: center;
    padding: 3px 14px;
    box-sizing: border-box;
    /* The radius has to grow with the height or the chip stops reading as a pill. */
    border-radius: 999px;
  }
`

const ConnectionTitle = styled.p`
  font-weight: 600;
  font-size: 18px;
  color: ${props => props.theme.colors.gray_900};
`

const ConnectionTableCell = styled.td`
  display: flex;
  width: 33.3%;
  margin-top: 12px;

  @media (max-width: ${BREAKPOINTS.phone}px) {
    width: 100%;
    min-width: 0;

    /* Spacer cells only exist to fill out a three-column row; stacked they are blank gaps. */
    &:empty {
      display: none;
    }
  }
`

const ConnectionLabel = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
  width: max(10vw, 128px);
`

const ConnectionValue = styled.p`
  font-weight: 600;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-top: -1px;
  min-width: 0;
  overflow-wrap: anywhere;
`

const ConnectionTableDivider = styled.p<{ $left: string }>`
  /* Column separators for a three-column table that becomes one column on a phone. */
  @media (max-width: ${BREAKPOINTS.phone}px) {
    display: none;
  }

  position: absolute;
  width: 0px;
  border: 1px solid ${props => props.theme.colors.gray_200};
  left: ${props => props.$left};
  top: 70px;
  bottom: 18px;
  margin: 0;
`

const StatCardsColumn = styled.div`
  flex: 0 0 35%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 10px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex: 1 1 auto;
    grid-template-rows: auto auto;
  }
`

const StatCard = styled.div<{ $color: string }>`
  border-radius: 24px;
  padding: 24px;
  background-color: ${props => props.theme.colors[props.$color]};
  display: flex;
  position: relative;
  min-height: 40px;
`

const StatCardBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
`

const StatCardIconContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  width: 18px;
  height: 18px;
  padding: 7px;
  border-radius: 100px;
  position: absolute;
  right: 24px;
  top: 24px;
`

const StatCardTitle = styled.p<{ $white?: boolean }>`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.$white ? props.theme.colors.white : props.theme.colors.dark};
  margin: 0px;
  padding-right: 40px;
`

const StatCardValue = styled.p<{ $white?: boolean }>`
  font-weight: 700;
  font-size: 24px;
  color: ${props => props.$white ? props.theme.colors.white : props.theme.colors.dark};
  margin: 0px;
  white-space: nowrap;
`

const StatCardUnit = styled.span`
  font-weight: 500;
  font-size: 15px;
  padding-left: 6px;
`

/* Owns the page's vertical space on desktop so the scroll panel below can take whatever the header
   leaves, instead of a magic calc() that has to guess the header's height. The old
   calc(90vh - 12.5vw - 130px) guessed low once the header grew, putting 212px of the panel - and its
   scrollbar - below the visible shell at 1280px. */
const PageColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: block;
    height: auto;
  }
`

const LowerScroller = styled.div`
  overflow: auto;
  flex: 1 1 auto;
  min-height: 0;

  /* The page itself scrolls below the shell breakpoint, so a nested scroll region here
     would trap the gesture and hide content behind an unreachable inner scrollbar. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    height: auto;
    overflow: visible;
  }
`

const CommentContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: 18px;
  border-radius: 24px;
  position: relative;
`

const CommentTitle = styled.p`
  font-weight: 600;
  font-size: 18px;
  color: ${props => props.theme.colors.gray_900};
`

const ModalContainer = styled.div`
  padding: 24px;
  height: 85dvh;
  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* A fixed height with no overflow of its own caps the parent Modal's scroll area, so anything
       past it is clipped AND unreachable. Size to content and let Modal's max-height scroll it. */
    height: auto;
  }
`

const ModalTitle = styled.p`
  font-weight: 600;
  font-size: 20px;
  margin: 0px;
  color: ${props => props.theme.colors.gray_900};
  width: min(max(20vw, 256px), calc(100vw - 24px));
`

const SmallModalContainer = styled.div`
  padding: 24px;
`

const SmallModalLabel = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
`

const SmallModalButtonContainer = styled.div`
  width: min(max(15vw, 192px), calc(100vw - 72px));
  display: flex;
`

const SmallModalErrorLabel = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.theme.colors.danger};
  width: 100%;
  text-align: center;
`

const ModalCloseButtonContainer = styled.div`
  position: absolute;
  top: 26px;
  right: 26px;
`

const AddCommentTextArea = styled.textarea`
  width: calc(100% - 4px);
  height: 120px;
  border-radius: 4px;
  border: none;
  outline: none;
`

const CommentMessageText = styled.p`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_500};
  max-width: 100%;
  margin: 0px;
  white-space: pre-wrap;
  white-space: -moz-pre-wrap;
  white-space: -o-pre-wrap;
  word-wrap: break-word;
`

const CommentAuthorText = styled.p`
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
`

const CommentDateText = styled.p`
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_400};
  margin: 0px;
`

const CurrentChargeLabel = styled.p`
  color: ${props => props.theme.dark};
  font-size: 24px;
  font-weight: 700;
`

const CurrentChargeElaplesTimeLabel = styled.p`
  color: ${props => props.theme.dark};
  font-size: 14px;
  font-weight: 400;
`

const CurrentChargePeriodContainer = styled.div`
  box-shadow: 0px 0px 10px 0px #0000001A;
  border-radius: 16px;
  padding: 20px;
  width: max(calc(20vw - 40px), 216px);
`

const CurrentChargePeriodTitle = styled.p`
  font-weight: 700;
  font-size: 18px;
  margin: 0px;
  color: ${props => props.theme.colors.gray_900};
`

const CurrentChargePeriodTime = styled.p`
  font-weight: 400;
  font-size: 14px;
  margin: 0px;
  color: ${props => props.theme.colors.gray_900};
`

const CurrentChargePeriodStatCard = styled.div`
  border-radius: 24px;
  padding: 14px;
  background-color: ${props => props.theme.colors.warning};
  display: flex;
  position: relative;
`

const CurrentChargePeriodStatCardIconContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  width: 28px;
  height: 28px;
  padding: 7px;
  border-radius: 100px;
`

const CurrentChargePeriodStatCardTitle = styled.p`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.dark};
  margin: 0px;
  margin-left: 18px;
`

const CurrentChargePeriodStatCardValue = styled.p`
  font-weight: 700;
  font-size: 24px;
  color: ${props => props.theme.colors.dark};
  margin: 0px;
  margin-left: 18px;
`

const CurrentChargePeriodStatCardUnit = styled.span`
  font-weight: 500;
  font-size: 15px;
  padding-left: 6px;
`

const CurrentChargeChargeDetailContainer = styled.div`
  width: 100%;
  display: flex;
  position: relative;
`

const CurrentChargeChargeDetailLabel = styled.p`
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-top: 4px;
  margin-left: 8px;
`

const CurrentChargeChargeDetailValue = styled.p`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  position: absolute;
  right: 0px;
  margin: 0px;
  margin-top: 4px;
`

const ChargeDetailContainer = styled.div`
  width: 100%;
  display: flex;
  position: relative;
`

const ChargeDetailLabel = styled.p`
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-top: 4px;
  margin-left: 8px;
`

const ChargeDetailValue = styled.p`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  position: absolute;
  right: 0px;
  margin: 0px;
  margin-top: 4px;
`

const SwitchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 32px;

  /* A switch is 31px tall, so an 8px row gap puts two of them 39px apart once the row wraps and a
     tap between them can land on the wrong one. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    row-gap: 16px;
  }
`

const SwitchItem = styled.div`
  display: flex;
  align-items: center;
`

const KwhPriceInputContainer = styled.div<{ $isSelected: boolean }>`
  width: calc(100% - 16px);
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  outline: 2px solid ${props => props.$isSelected ? props.theme.colors.primary : props.theme.colors.gray_300};
  box-shadow: 0px 0px ${props => props.$isSelected ? '6' : '0'}px ${props => props.theme.colors.primary};
`

const KwhPriceError = styled.p`
  font-size: 12px;
  margin: 6px 0px 0px 8px;
  color: ${props => props.theme.colors.danger};
`

export default function GeneralInformation({ charger, isAdmin, triggerRefreshChargerValues }: { charger: ChargerType, isAdmin: boolean, triggerRefreshChargerValues: () => void }) {
  const { t, i18n } = useTranslation()
  const confirm = useConfirm()
  const { addNotification } = useNotification()

  const [charges, setCharges] = useState<ChargeType[]>()
  const [chargerGroup, setChargerGroup] = useState<string>()

  const [evseState, setEvseState] = useState<EvseStateType | null>()
  const [chargerComments, setChargerComments] = useState<{ [key: string]: { author: string, comment: string } }>()
  const [isShowingNewCommentModal, setIsShowingNewCommentModal] = useState<boolean>(false)
  const [newCommentInputValue, setNewCommentInputValue] = useState<string>()
  const [chargerDetails, setChargerDetails] = useState<{ connectionData: { wifi?: { ip: string, mac: string }, ethernet?: { ip: string, mac: string }, gsm?: { status: string } }, dlmState: string, midMeterState: string, lifetimeRealySwitches: number, lifetimeChgSec: number, lifetimeWh: number, rfidAccessMode: 0 | 1 | 2 | 3 | 4, rfidAvailable: boolean }>()
  const [selectedConnection, setSelectedConnection] = useState<'wifi' | 'ethernet' | 'gsm'>('ethernet')
  const [isShowingDeviceInformation, setIsShowingDeviceInformation] = useState<boolean>(false)
  const [isShowingCurrentChargeDetails, setIsShowingCurrentChargeDetails] = useState<boolean>(false)
  const [currentChargeDetails, setCurrentChargeDetails] = useState<ChargeType>()
  const [currentChargeDetailsLastRefresh, setCurrentChargeDetailsLastRefresh] = useState<number>()

  const [isShowingEditChargerNameModal, setIsShowingEditChargerNameModal] = useState<boolean>(false)
  const [isEditingChargerNameModalLoading, setIsEditingChargerNameModalLoading] = useState<boolean>(false)
  const [chargerNameInputValue, setChargerNameInputValue] = useState<string>(charger.localName)

  const [specificKwhPrice, setSpecificKwhPrice] = useState<{ currency: string, value: number }>()
  const [uniformKwhPrice, setUniformKwhPrice] = useState<{ currency: string, value: number }>()
  const [isShowingKwhPriceModal, setIsShowingKwhPriceModal] = useState<boolean>(false)
  const [isKwhPriceUniform, setIsKwhPriceUniform] = useState<boolean>(false)
  // The modal edits text, not numbers: a field turned back into a number on every keystroke cannot hold
  // "70." or "70,1" on the way to 70,10 (VLT-3049). The text is only parsed on save.
  const [kwhPriceDraft, setKwhPriceDraft] = useState<{ uniform: string, uniformCurrency: string, specific: string, specificCurrency: string }>({ uniform: '', uniformCurrency: '', specific: '', specificCurrency: '' })
  const [kwhPriceErrors, setKwhPriceErrors] = useState<{ uniform: boolean, specific: boolean }>({ uniform: false, specific: false })

  // Statistics email recipients + subscription toggle (VLT-2128)
  const [isShowingStatisticsEmailModal, setIsShowingStatisticsEmailModal] = useState<boolean>(false)
  const [statisticsEmailEnabled, setStatisticsEmailEnabled] = useState<boolean>(false)
  const [statisticsEmails, setStatisticsEmails] = useState<string[]>([])
  const [newStatisticsEmailInput, setNewStatisticsEmailInput] = useState<string>('')
  const [statisticsEmailError, setStatisticsEmailError] = useState<string | null>(null)

  const [isAssigningChargerToUser, setIsAssigningChargerToUser] = useState<boolean>(false)
  const [isAssigningChargerToUserModalLoading, setAssigningChargerToUserModalLoading] = useState<boolean>(false)
  const [assigningChargerToUserInputValue, setAssigningChargerToUserInputValue] = useState<string>("")
  const [assigningChargerToUserError, setAssigningChargerToUserError] = useState<string | null>()

  const [evseStateErrorTranslationTable] = useState<{ [key: number]: string }>({
    13: "vehicle error",
    5: "diode check failed",
    6: "GFCI fault",
    7: "no ground",
    8: "stuck realy",
    9: "GFI self-test failure",
    10: "over temperature error",
    11: "over current error",
    12: "error on I²C bus",
    14: "over humidity error",
    15: "input power phase misconnected",
    16: "overvoltage error",
    17: "undervoltage error"
  })

  async function fetchCharges() {
    const response = await Comm('/api-cdr/cdrs/last', {
      headers: { "Charger_ID": charger.chargerId }
    })
    const result = await response.json()
    result.reverse()
    setCharges(result)
  }

  async function fetchEvseState() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 3 })
    })

    if (response.ok) {
      const result = await response.json()
      const { session, ...resultWithoutSession } = result
      setEvseState(resultWithoutSession)

      // Set current charge details if charger is charging
      // (A charger can also mean its not currently charging but is available to charge (evse_state 2), so we dont check if chg_on)
      if (charger.online && (result.evse_state == 2 || result.evse_state == 3 || result.evse_state == 4) && /*result.chg_on &&*/ result.session) {
        setCurrentChargeDetails(result.session)
      } else {
        setIsShowingCurrentChargeDetails(false)
      }
    }
  }

  async function fetchChargerComments() {
    const dbRef = ref(getDatabase(fbConf))
    const response = await get(child(dbRef, 'voltie/updategroups'))
    const result = response.val()

    for (const group in result) {
      for (const chargerInGroup in result[group].chargers) {
        if (chargerInGroup == charger.chargerId) {
          setChargerGroup(group)
          if (result[group].chargers[chargerInGroup].comments) {
            setChargerComments(result[group].chargers[chargerInGroup].comments)
          }
        }
      }
    }
  }

  async function fetchChargerDetails() {
    const CMD_GET_PRODUCT_NFO_response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 12 })
    })
    const CMD_GET_PRODUCT_NFO_result = await CMD_GET_PRODUCT_NFO_response.json()

    const CMD_GET_CONNECTION_STAT_response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 22 })
    })
    const CMD_GET_CONNECTION_STAT_result = await CMD_GET_CONNECTION_STAT_response.json()

    const CMD_RFID_GETLIST_response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 23 })
    })
    const CMD_RFID_GETLIST_result = await CMD_RFID_GETLIST_response.json()

    const connectionData: { wifi?: { ip: string, mac: string }, ethernet?: { ip: string, mac: string }, gsm?: { status: string } } = {}
    if (CMD_GET_CONNECTION_STAT_result.wifi) {
      connectionData.wifi = { ip: CMD_GET_CONNECTION_STAT_result.wifi.ip || '-', mac: CMD_GET_CONNECTION_STAT_result.wifi.mac || '-' }
    }
    if (CMD_GET_CONNECTION_STAT_result.ethernet) {
      connectionData.ethernet = { ip: CMD_GET_CONNECTION_STAT_result.ethernet.ip_address || '-', mac: CMD_GET_CONNECTION_STAT_result.ethernet.mac_address || '-' }
    }
    if (CMD_GET_CONNECTION_STAT_result.gsm) {
      connectionData.gsm = { status: CMD_GET_CONNECTION_STAT_result.gsm.status || '-' }
    }

    // Auto-select the connection tab with actual data, priority: Ethernet > WiFi > GSM
    if (connectionData.ethernet?.ip && connectionData.ethernet.ip != '-') {
      setSelectedConnection('ethernet')
    } else if (connectionData.wifi?.ip && connectionData.wifi.ip != '-') {
      setSelectedConnection('wifi')
    } else if (connectionData.gsm?.status && connectionData.gsm.status != '-') {
      setSelectedConnection('gsm')
    }

    setChargerDetails({
      connectionData,
      dlmState: CMD_GET_PRODUCT_NFO_result?.dlm_state.toString(),
      midMeterState: CMD_GET_PRODUCT_NFO_result?.mid_state.toString(),
      lifetimeRealySwitches: CMD_GET_PRODUCT_NFO_result.lifetime?.lifetime_switch,
      lifetimeChgSec: CMD_GET_PRODUCT_NFO_result.lifetime?.lifetime_chg_sec,
      lifetimeWh: CMD_GET_PRODUCT_NFO_result.lifetime?.lifetime_Wh,
      rfidAccessMode: CMD_RFID_GETLIST_result.rfid_stat?.access_mode,
      rfidAvailable: CMD_RFID_GETLIST_result.rfid_stat?.rfid_available
    })
  }

  async function fetchKwhPrice() {
    const dbRef = ref(getDatabase(fbConf))

    const specificKwhPriceResponse = await get(child(dbRef, `voltie/chargerusers/${isAdmin ? charger.userId : getAuth(fbConf).currentUser?.uid}/chargers/${charger.chargerId}/kwh_price`))
    setIsKwhPriceUniform(!specificKwhPriceResponse.exists())
    const specificVal = specificKwhPriceResponse.val()
    setSpecificKwhPrice(specificVal ? { ...specificVal, currency: normalizeCurrency(specificVal.currency) } : undefined)

    const uniformKwhPriceResponse = await get(child(dbRef, `voltie/chargerusers/${isAdmin ? charger.userId : getAuth(fbConf).currentUser?.uid}/preferences/calculation_values/price`))
    const uniformVal = uniformKwhPriceResponse.val()
    setUniformKwhPrice(uniformVal ? { ...uniformVal, currency: normalizeCurrency(uniformVal.currency) } : undefined)
  }

  function openKwhPriceModal() {
    const fallbackCurrency = defaultCurrencyForLanguage(i18n.language)
    setKwhPriceDraft({
      uniform: uniformKwhPrice ? priceInputText(Number(uniformKwhPrice.value), i18n.language) : '',
      uniformCurrency: uniformKwhPrice?.currency || fallbackCurrency,
      specific: specificKwhPrice ? priceInputText(Number(specificKwhPrice.value), i18n.language) : '',
      specificCurrency: specificKwhPrice?.currency || uniformKwhPrice?.currency || fallbackCurrency,
    })
    setKwhPriceErrors({ uniform: false, specific: false })
    setIsShowingKwhPriceModal(true)
  }

  async function saveKwhPrice() {
    if (isAdmin) {
      alert("You are not permitted to set kWh prices as an admin")
      fetchKwhPrice()
      setIsShowingKwhPriceModal(false)
      return
    }

    // The uniform price is written on every save. Only an account that never had one, setting a charger-specific
    // price, may leave it empty: the charger does not use it, and requiring it would invent a price for all the others.
    const uniformValue = kwhPriceDraft.uniform.trim() === '' && !uniformKwhPrice && !isKwhPriceUniform ? undefined : parsePrice(kwhPriceDraft.uniform)
    const specificValue = isKwhPriceUniform ? undefined : parsePrice(kwhPriceDraft.specific)
    const errors = { uniform: uniformValue === null, specific: specificValue === null }
    setKwhPriceErrors(errors)
    if (errors.uniform || errors.specific) return

    const uid = getAuth(fbConf).currentUser?.uid
    if (uniformValue != null) {
      try {
        await set(ref(getDatabase(fbConf), `voltie/chargerusers/${uid}/preferences/calculation_values/price`), { value: uniformValue, currency: normalizeCurrency(kwhPriceDraft.uniformCurrency) })
      } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
    }
    try {
      await set(ref(getDatabase(fbConf), `voltie/chargerusers/${uid}/chargers/${charger.chargerId}/kwh_price`),
        specificValue == null ? null : { value: specificValue, currency: normalizeCurrency(kwhPriceDraft.specificCurrency) })
    } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }

    await fetchKwhPrice()
    setIsShowingKwhPriceModal(false)
  }

  // Statistics email recipients are a user preference; admins manage the charger owner's, users their own (VLT-2128)
  function statisticsEmailUid() {
    return isAdmin ? charger.userId : getAuth(fbConf).currentUser?.uid
  }

  async function fetchStatisticsEmailSettings() {
    const dbRef = ref(getDatabase(fbConf))
    const emailsResponse = await get(child(dbRef, `voltie/chargerusers/${statisticsEmailUid()}/preferences/additional_email`))
    const emailsVal = emailsResponse.val()
    setStatisticsEmails(Array.isArray(emailsVal) ? emailsVal : emailsVal ? Object.values(emailsVal) : [])
    const flagResponse = await get(child(dbRef, `voltie/chargerusers/${statisticsEmailUid()}/preferences/push_notifications/statistics_email`))
    setStatisticsEmailEnabled(!!flagResponse.val())
  }

  async function addStatisticsEmail() {
    const email = newStatisticsEmailInput.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatisticsEmailError(t('login_invalidEmail'))
      return
    }
    if (email === (charger.userEmail || '').toLowerCase() || statisticsEmails.includes(email)) {
      setStatisticsEmailError(t('charger_duplicateEmail'))
      return
    }
    const updated = [...statisticsEmails, email]
    setStatisticsEmails(updated)
    setNewStatisticsEmailInput('')
    setStatisticsEmailError(null)
    try {
      await set(ref(getDatabase(fbConf), `voltie/chargerusers/${statisticsEmailUid()}/preferences/additional_email`), updated)
    } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
  }

  async function removeStatisticsEmail(email: string) {
    const updated = statisticsEmails.filter(e => e !== email)
    setStatisticsEmails(updated)
    try {
      await set(ref(getDatabase(fbConf), `voltie/chargerusers/${statisticsEmailUid()}/preferences/additional_email`), updated)
    } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
  }

  async function setStatisticsEmailSubscription(enabled: boolean) {
    setStatisticsEmailEnabled(enabled)
    try {
      await set(ref(getDatabase(fbConf), `voltie/chargerusers/${statisticsEmailUid()}/preferences/push_notifications/statistics_email`), enabled)
    } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
  }

  useEffect(() => {
    async function fetchCharger() {
      await fetchCharges()
      await fetchKwhPrice()
      await fetchStatisticsEmailSettings()

      // Only fetch comments for admins for now, firebase is not responding to users
      if (isAdmin) {
        await fetchChargerComments()
      }

      if (charger.online) {
        await fetchEvseState()
        await fetchChargerDetails()
      }
    }
    fetchCharger()
  }, [])

  function chargerDlmAndMidStateCodeToMeaning(code: string) {
    switch (parseInt(code)) {
      case 0: return t('charger_dlmMidStates_UNAVAILABLE_ERROR')
      case 1: return t('charger_dlmMidStates_UNAVAILABLE')
      case 2: return t('charger_dlmMidStates_CONNECTED_NOT_IN_USE')
      case 3: return t('charger_dlmMidStates_CONNECTED_IN_USE')
    }
  }

  async function startCharging() {
    await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 5 })
    })
    setEvseState(null)
    setTimeout(fetchEvseState, 3000)
  }

  async function stopCharging() {
    await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 6 })
    })
    setEvseState(null)
    setTimeout(fetchEvseState, 3000)
  }

  async function addChargerComment() {
    try {
      await set(ref(getDatabase(fbConf), `voltie/updategroups/${chargerGroup}/chargers/${charger.chargerId}/comments/${Date.now()}`), {
        author: getAuth(fbConf).currentUser?.email,
        comment: newCommentInputValue || ''
      })
    } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }

    setIsShowingNewCommentModal(false)
    setNewCommentInputValue('')
    fetchChargerComments()
  }

  async function handleUpdateChargerName() {
    setIsEditingChargerNameModalLoading(true)
    if (chargerNameInputValue.length > 0) {
      if (isAdmin) {
        await Comm('/api-charger/charger/update', {
          method: 'PUT',
          body: JSON.stringify({
            chargerId: charger.chargerId,
            serialNumber: charger.serialNumber || "",
            firmwareVersion: '',
            softwareVersion: '',
            osVersion: '',
            localName: chargerNameInputValue,
            initedCharger: {
              chargerId: '',
              userId: ''
            }
          }),
          headers: {
            'charger_id': charger.chargerId,
            'Content-Type': 'application/json'
          }
        })
      } else {
        try {
          await set(ref(getDatabase(fbConf), `voltie/chargerusers/${getAuth(fbConf).currentUser?.uid}/chargers/${charger.chargerId}/name`), chargerNameInputValue)
        } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
      }
    } else {
      setChargerNameInputValue(charger.localName)
    }

    setIsShowingEditChargerNameModal(false)
    triggerRefreshChargerValues()
    setIsEditingChargerNameModalLoading(false)
  }

  async function handleChargerUpdateBlocked(blocked: boolean) {
    await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 62, "available": !blocked })
    })
    setEvseState(null)
    setTimeout(async () => {
      await fetchChargerDetails()
      fetchEvseState()
    }, 3000)
  }

  async function handleUpdateAutostart(autostart: boolean) {
    if (evseState && (evseState.autostart === 1 || evseState.autostart === 0)) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 4, "chg_mode": autostart ? 1 : 0 })
      })
    }
  }

  async function handleUpdateRFIDReader(rfidReaderOn: boolean) {
    if (chargerDetails && chargerDetails.rfidAvailable && (chargerDetails.rfidAccessMode === 0 || chargerDetails.rfidAccessMode === 1)) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 65, "access_mode": rfidReaderOn ? 1 : 0 })
      })
    }
  }

  async function handleAssignChargerToUser() {
    setAssigningChargerToUserModalLoading(true)
    if (assigningChargerToUserInputValue) {

      // Get UID by email
      const result = await Comm(`/api-admin/chargerusers/email?email=${assigningChargerToUserInputValue}`, {})
      if (result.status === 200) {
        const userId = await result.text()

        // Send UID to charger
        await Comm('/api-websocket/mobile', {
          method: "POST",
          headers: {
            "Charger_id": charger.chargerId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ "msg_type": 71, "user_id": userId, "forced": true })
        })

        // Add charger to the users firebase node
        try {
          await set(ref(getDatabase(fbConf), `voltie/chargerusers/${userId}/chargers/${charger.chargerId}`), { access_lvl: 7, hostname: `voltiecharger-${charger.chargerId.slice(-4)}`, name: `voltiecharger-${charger.chargerId.slice(-4)}` })
        } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }

      } else {
        setAssigningChargerToUserError("Unknown email")
        setAssigningChargerToUserModalLoading(false)
        return
      }
    } else {
      setAssigningChargerToUserError("Unknown email")
      setAssigningChargerToUserModalLoading(false)
      return
    }
    setAssigningChargerToUserModalLoading(false)
    setIsAssigningChargerToUser(false)
    window.location.reload()
  }

  async function handleSyncChargerNameToFirebaseName() {
    if (isAdmin) {
      if (await confirm("Ezzel felülírod a töltő localon szereplő nevét a Firebaseben szereplő nevével")) {
        try {
          const firebaseNameResponse = await get(ref(getDatabase(fbConf), `voltie/chargerusers/${charger.userId}/chargers/${charger.chargerId}/name`))
          const firebaseName = firebaseNameResponse.val()
          await Comm('/api-charger/charger/update', {
            method: 'PUT',
            body: JSON.stringify({
              chargerId: charger.chargerId,
              serialNumber: charger.serialNumber || "",
              firmwareVersion: '',
              softwareVersion: '',
              osVersion: '',
              localName: firebaseName,
              initedCharger: {
                chargerId: '',
                userId: ''
              }
            }),
            headers: {
              'charger_id': charger.chargerId,
              'Content-Type': 'application/json'
            }
          })
          setChargerNameInputValue(firebaseName)
          setTimeout(triggerRefreshChargerValues, 1000)
        } catch {
          addNotification("Something went wrong", "TEXT", "error")
        }
      }
    }
  }

  async function handleSyncFirebaseNameToChargerName() {
    if (isAdmin) {
      if (await confirm("Ezzel felülírod a töltő Firebaseben szereplő nevét a localon szereplő nevével")) {
        try {
          await set(ref(getDatabase(fbConf), `voltie/chargerusers/${charger.userId}/chargers/${charger.chargerId}/name`), chargerNameInputValue)
          setTimeout(triggerRefreshChargerValues, 1000)
          addNotification("Done", "TEXT", "normal")
        } catch {
          addNotification("Something went wrong", "TEXT", "error")
        }
      }
    }
  }

  // Fetch current charge details every 5 seconds if the current charge details modal is open
  useEffect(() => {
    const evseStateRefresh = setTimeout(() => {
      if (isShowingCurrentChargeDetails) {
        fetchEvseState()
        setCurrentChargeDetailsLastRefresh(Date.now())
      }
    }, 5000)

    return () => clearTimeout(evseStateRefresh)
  }, [currentChargeDetailsLastRefresh, isShowingCurrentChargeDetails])

  const shownKwhPrice = isKwhPriceUniform ? uniformKwhPrice : specificKwhPrice

  return (
    <>
      {isShowingKwhPriceModal && (
        <Modal onEscape={() => { setIsShowingKwhPriceModal(false); fetchKwhPrice() }}>
          <SmallModalContainer>
            <KwhPriceInputContainer $isSelected={isKwhPriceUniform} onClick={() => setIsKwhPriceUniform(true)}>
              <InputWithLabel label={`${t('charger_uniform')} ${t('charger_kwhPrice')}`} width="min(280px, calc(100vw - 210px))" inputMode="decimal" value={kwhPriceDraft.uniform} isError={kwhPriceErrors.uniform}
                onInput={value => { setKwhPriceDraft(draft => ({ ...draft, uniform: value })); setKwhPriceErrors(errors => ({ ...errors, uniform: false })) }} />
              <WhiteSpace width="8px" />
              <Select width="96px" options={currencyOptions(kwhPriceDraft.uniformCurrency)} handleSelect={value => setKwhPriceDraft(draft => ({ ...draft, uniformCurrency: value }))} isAbsolute />
            </KwhPriceInputContainer>
            {kwhPriceErrors.uniform && <KwhPriceError>{t('charger_enterAValidPrice')}</KwhPriceError>}
            <WhiteSpace height="16px" />

            <KwhPriceInputContainer $isSelected={!isKwhPriceUniform} onClick={() => setIsKwhPriceUniform(false)}>
              <InputWithLabel label={`${t('charger_chargerSpecific')} ${t('charger_kwhPrice')}`} width="min(280px, calc(100vw - 210px))" inputMode="decimal" value={kwhPriceDraft.specific} isError={kwhPriceErrors.specific}
                onInput={value => { setKwhPriceDraft(draft => ({ ...draft, specific: value })); setKwhPriceErrors(errors => ({ ...errors, specific: false })) }} />
              <WhiteSpace width="8px" />
              <Select width="96px" options={currencyOptions(kwhPriceDraft.specificCurrency)} handleSelect={value => setKwhPriceDraft(draft => ({ ...draft, specificCurrency: value }))} isAbsolute />
            </KwhPriceInputContainer>
            {kwhPriceErrors.specific && <KwhPriceError>{t('charger_enterAValidPrice')}</KwhPriceError>}

            <WhiteSpace height="16px" />
            <SmallModalButtonContainer>
              <Button onclick={saveKwhPrice} label={t('charger_save')} width="calc(50% - 8px)" bgcolor="#4AB1FC" labelcolor="white" />
              <WhiteSpace width="16px" />
              <Button onclick={() => { setIsShowingKwhPriceModal(false); fetchKwhPrice() }} label={t('charger_cancel')} width="calc(50% - 8px)" />
            </SmallModalButtonContainer>
          </SmallModalContainer>
        </Modal>
      )}
      {isShowingStatisticsEmailModal && (
        <Modal onEscape={() => setIsShowingStatisticsEmailModal(false)}>
          <SmallModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingStatisticsEmailModal(false)} />
            </ModalCloseButtonContainer>
            <SmallModalLabel>{t('charger_statisticsEmail')}</SmallModalLabel>
            <WhiteSpace height="16px" />
            <div style={{ display: 'flex' }}>
              <GeneralDetailValue style={{ marginTop: '4px' }}>{t('charger_receiveStatisticsEmail')}</GeneralDetailValue>
              <div style={{ marginLeft: 'auto' }}>
                <Switch toggled={statisticsEmailEnabled} setToggled={setStatisticsEmailSubscription} />
              </div>
            </div>
            <WhiteSpace height="16px" />
            {charger.userEmail && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <GeneralDetailValue style={{ fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{charger.userEmail}</GeneralDetailValue>
                <WhiteSpace width="8px" />
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#A1A5B7', whiteSpace: 'nowrap' }}>{t('charger_default')}</span>
              </div>
            )}
            {statisticsEmails.filter(email => email.toLowerCase() !== (charger.userEmail || '').toLowerCase()).map(email => (
              <div key={email} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <GeneralDetailValue style={{ fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</GeneralDetailValue>
                <WhiteSpace width="8px" />
                <Icon path="general.x" width="14px" height="14px" color="danger" onClick={() => removeStatisticsEmail(email)} />
              </div>
            ))}
            <WhiteSpace height="8px" />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <InputWithLabel label="Email" width={"calc(70% - 8px)"} value={newStatisticsEmailInput} onInput={(value) => { setNewStatisticsEmailInput(value); setStatisticsEmailError(null) }} onKeyDown={(e) => { if (e.key === 'Enter') addStatisticsEmail() }} />
              <WhiteSpace width="8px" />
              <Button onclick={addStatisticsEmail} label={t('charger_add')} width="30%" bgcolor="#4AB1FC" labelcolor="white" />
            </div>
            {statisticsEmailError && <>
              <WhiteSpace height="8px" />
              <SmallModalErrorLabel>{statisticsEmailError}</SmallModalErrorLabel>
            </>}
          </SmallModalContainer>
        </Modal>
      )}
      {isShowingNewCommentModal && (
        <Modal onEscape={() => setIsShowingNewCommentModal(false)}>
          <SmallModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingNewCommentModal(false)} />
            </ModalCloseButtonContainer>
            <SmallModalLabel>{t('charger_addNewComment')}</SmallModalLabel>
            <WhiteSpace height="16px" />
            <AddCommentTextArea value={newCommentInputValue} onInput={(e) => setNewCommentInputValue((e.target as HTMLInputElement).value)}></AddCommentTextArea>
            <WhiteSpace height="8px" />
            <SmallModalButtonContainer>
              <Button onclick={addChargerComment} label={t('charger_add')} width="calc(50% - 8px)" bgcolor="#4AB1FC" labelcolor="white" />
              <WhiteSpace width="16px" />
              <Button onclick={() => setIsShowingNewCommentModal(false)} label={t('charger_cancel')} width="calc(50% - 8px)" />
            </SmallModalButtonContainer>
          </SmallModalContainer>
        </Modal>
      )}
      {isShowingEditChargerNameModal && (
        <Modal onEscape={() => setIsShowingEditChargerNameModal(false)}>
          <SmallModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingEditChargerNameModal(false)} />
            </ModalCloseButtonContainer>
            <SmallModalLabel>{t('charger_editingChargerName')}</SmallModalLabel>
            <WhiteSpace height="16px" />
            <InputWithLabel label={t('charger_chargerName')} width={"100%"} value={chargerNameInputValue} onInput={(value) => setChargerNameInputValue(value.replace(/[:\\\/\?\*\[\]]/g, "").slice(0, 30))} />
            <WhiteSpace height="12px" />
            <SmallModalButtonContainer>
              <Button onclick={() => { setIsShowingEditChargerNameModal(false); setChargerNameInputValue(charger.localName) }} label={t('charger_cancel')} width="calc(50% - 8px)" />
              <WhiteSpace width="16px" />
              <Button onclick={isEditingChargerNameModalLoading ? () => { } : handleUpdateChargerName} label={isEditingChargerNameModalLoading ? <LoaderDots /> : t('charger_save')} width="calc(50% - 8px)" bgcolor="#4AB1FC" labelcolor="white" />
            </SmallModalButtonContainer>
          </SmallModalContainer>
        </Modal>
      )}
      {isAssigningChargerToUser && (
        <Modal onEscape={() => setIsAssigningChargerToUser(false)}>
          <SmallModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsAssigningChargerToUser(false)} />
            </ModalCloseButtonContainer>
            <SmallModalLabel>{t('charger_assignToUser')}</SmallModalLabel>
            <WhiteSpace height="16px" />
            <InputWithLabel label="Email" width={"100%"} value={assigningChargerToUserInputValue} onInput={(value) => setAssigningChargerToUserInputValue(value)} />
            {assigningChargerToUserError && <>
              <WhiteSpace height="12px" />
              <SmallModalErrorLabel>{assigningChargerToUserError}</SmallModalErrorLabel>
            </>}
            <WhiteSpace height="12px" />
            <SmallModalButtonContainer>
              <Button onclick={() => { setIsAssigningChargerToUser(false); setAssigningChargerToUserInputValue("") }} label={t('charger_cancel')} width="calc(50% - 8px)" />
              <WhiteSpace width="16px" />
              <Button onclick={isAssigningChargerToUserModalLoading ? () => { } : handleAssignChargerToUser} label={isAssigningChargerToUserModalLoading ? <LoaderDots /> : t('charger_save')} width="calc(50% - 8px)" bgcolor="#4AB1FC" labelcolor="white" />
            </SmallModalButtonContainer>
          </SmallModalContainer>
        </Modal>
      )}
      {isShowingCurrentChargeDetails && currentChargeDetails && (
        <Modal align="right" onEscape={() => setIsShowingCurrentChargeDetails(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingCurrentChargeDetails(false)} />
            </ModalCloseButtonContainer>
            {evseState?.evse_state == 2 || ((evseState?.evse_state == 3 || evseState?.evse_state == 4) && !evseState?.chg_on) ? <>
              <ModalTitle>{t('charger_carPluggedIn')}</ModalTitle>
            </> : <>
              <ModalTitle>{t('charger_chargingInProgress')}</ModalTitle>
            </>}
            <WhiteSpace height="12px" />
            <div style={{ display: "flex", width: "95%", alignItems: "center", justifyContent: "center" }}>
              <Icon path="chargers.power" width="28px" height="28px" color="success" style={{ filter: 'drop-shadow(0px 0px 6px #57D9BA)' }} />
              <WhiteSpace width="8px" />
              {evseState?.evse_state == 2 || ((evseState?.evse_state == 3 || evseState?.evse_state == 4) && !evseState?.chg_on) ? <>
                <CurrentChargeLabel>{t('charger_pluggedIn')}</CurrentChargeLabel>
              </> : <>
                <CurrentChargeLabel>{t('charger_charging')}</CurrentChargeLabel>
              </>}
            </div>

            <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", marginTop: "-12px" }}>
              {currentChargeDetails.idtag_name && currentChargeDetails.idtag_name != '' && <>
                <Icon path="chargers.rfidTag" width="18px" height="18px" color="white" />
                <WhiteSpace width="4px" />
                <StatusLabel $color="#181C32">{currentChargeDetails.idtag_name || '-'}</StatusLabel>
                <WhiteSpace width="24px" />
              </>}
              <CurrentChargeElaplesTimeLabel>{`${t('charger_elapsedTime')}: ${new Date(currentChargeDetails.chg_time * 1000).toISOString().slice(11, 19)}`}</CurrentChargeElaplesTimeLabel>
            </div>

            <WhiteSpace height="18px" />
            <div style={{ display: 'flex', width: "100%" }}>
              <WhiteSpace width='3%' />
              {evseState?.evse_state == 2 || ((evseState?.evse_state == 3 || evseState?.evse_state == 4) && !evseState?.chg_on) ? <>
                <SlideToConfirm key={"startChargingSlider"} width='94%' height='48px' label="Slide to START charging" onConfirm={startCharging} icon="chargers.power" iconColor="primary" bgColorLeft="#2A6FF4" bgColorRight="#56F2DF" />
              </> : <>
                <SlideToConfirm key={"stopChargingSlider"} width='94%' height='48px' label="Slide to STOP charging" onConfirm={stopCharging} icon="chargers.power" iconColor="primary" bgColorLeft="#2A6FF4" bgColorRight="#E2594B" />
              </>}
            </div>
            <WhiteSpace height="24px" />

            <CurrentChargePeriodStatCard>
              <CurrentChargePeriodStatCardIconContainer>
                <Icon path="chargers.energy" color="warning" width="100%" height="100%" />
              </CurrentChargePeriodStatCardIconContainer>
              <div>
                <CurrentChargePeriodStatCardTitle>{t('charger_allEnergy')}</CurrentChargePeriodStatCardTitle>
                <CurrentChargePeriodStatCardValue>
                  {currentChargeDetails.chg_energy}
                  <CurrentChargePeriodStatCardUnit>kWh</CurrentChargePeriodStatCardUnit>
                </CurrentChargePeriodStatCardValue>
              </div>
            </CurrentChargePeriodStatCard>

            <WhiteSpace height="24px" />

            <div style={{ overflow: 'scroll', height: 'calc(85dvh - 48px - 253px)', marginLeft: '-24px', paddingLeft: '24px', marginRight: '-24px', paddingRight: '24px' }}>

              {[
                { icon: "chargers.pluggedIn", iconColor: 'gray_700', label: t('charger_pluggedIn'), value: new Date(currentChargeDetails.s_start * 1000).toLocaleString('hu') },
                { icon: "chargers.sleep", iconColor: 'gray_500', label: t('charger_idleTime'), value: secondsToHumanReadable(currentChargeDetails.idle_time) },
                { icon: "chargers.average", label: t('charger_averagePower'), value: `${currentChargeDetails.avg_power} kW` },
                { icon: "chargers.power", iconColor: 'warning', label: t('charger_currentPower'), value: `${evseState?.chg_power} kW` || '-' },
                { icon: "settings.voltage", iconColor: 'warning', label: t('charger_voltagePhase'), value: `${evseState?.u_eff} V` || '-' },
                { icon: "settings.amper", iconColor: 'primary', label: `${t('charger_amper')} (${t('charger_current')}/max)`, value: `${evseState?.chg_current} A / ${evseState?.max_current} A` || '-' },
                { adminOnly: true, icon: "chargers.humidity", iconColor: 'primary', label: `${t('charger_humidity')} (max/min)`, value: `${currentChargeDetails.hum_max}% / ${currentChargeDetails.hum_min}%` },
                { icon: "general.more", iconColor: 'gray_700', label: t('charger_phase'), value: evseState?.phase || '-' },
                { icon: "chargers.power", iconColor: 'gray_700', label: t('charger_autostart'), value: (evseState?.autostart === 1 ? t('general_on') : t('general_off')) || '-' },
                { adminOnly: true, icon: "chargers.thermometer", iconColor: 'danger', label: `MCU ${t('charger_temperature')} (${t('charger_avg')}/max)`, value: `${currentChargeDetails.temp_uc_avg.toFixed(1)} °C / ${currentChargeDetails.temp_uc_max.toFixed(1)} °C` || '-' },
                { adminOnly: true, icon: "chargers.thermometer", iconColor: 'danger', label: `POW ${t('charger_temperature')} (${t('charger_avg')}/max)`, value: `${currentChargeDetails.temp_pow_avg.toFixed(1)} °C / ${currentChargeDetails.temp_pow_max.toFixed(1)} °C` || '-' },
                { adminOnly: true, icon: "chargers.thermometer", iconColor: 'danger', label: `PI ${t('charger_temperature')} (${t('charger_avg')}/max)`, value: `${currentChargeDetails.temp_pi_avg.toFixed(1)} °C / ${currentChargeDetails.temp_pi_max.toFixed(1)} °C` || '-' },
              ].map(dataPoint => ((isAdmin && dataPoint.adminOnly) || !dataPoint.adminOnly) && (
                <React.Fragment >
                  <ChargeDetailContainer key={dataPoint.label}>
                    <Icon path={dataPoint.icon} color={dataPoint.iconColor} width="24px" height="24px" />
                    <ChargeDetailLabel>{dataPoint.label}</ChargeDetailLabel>
                    <ChargeDetailValue>{dataPoint.value}</ChargeDetailValue>
                  </ChargeDetailContainer>
                  <WhiteSpace height="16px" />
                </React.Fragment>
              ))}

              <div style={{ width: '100%', backgroundColor: '#E2E8F0', height: '1px', marginTop: '16px', marginBottom: '16px' }} />

              <CurrentChargePeriodContainer>
                <CurrentChargePeriodTitle>{t('charger_currentPeriod')}</CurrentChargePeriodTitle>
                <div style={{ width: '100%', backgroundColor: '#E2E8F0', height: '1px', marginTop: '16px', marginBottom: '16px' }} />
                <CurrentChargeChargeDetailContainer>
                  <Icon path='chargers.energy' width="24px" height="24px" color="warning" />
                  <CurrentChargeChargeDetailLabel>{t('charger_currentEnergy')}</CurrentChargeChargeDetailLabel>
                  <CurrentChargeChargeDetailValue>{`${currentChargeDetails.periods.at(-1)?.p_energy || 0} kWh`}</CurrentChargeChargeDetailValue>
                </CurrentChargeChargeDetailContainer>
                <WhiteSpace height="16px" />
                <CurrentChargeChargeDetailContainer>
                  <Icon path='chargers.time' width="24px" height="24px" color="primary" />
                  <CurrentChargeChargeDetailLabel>{t('charger_startTime')}</CurrentChargeChargeDetailLabel>
                  <CurrentChargeChargeDetailValue>{currentChargeDetails.periods.at(-1)?.p_start ? new Date((currentChargeDetails.periods.at(-1)?.p_start || 0) * 1000).toLocaleTimeString('hu') : '-'}</CurrentChargeChargeDetailValue>
                </CurrentChargeChargeDetailContainer>
              </CurrentChargePeriodContainer>
              {structuredClone(currentChargeDetails.periods).reverse().map(period => period.index != currentChargeDetails.periods.length - 1 && (
                <>
                  <WhiteSpace height="16px" />
                  <CurrentChargePeriodContainer>
                    <CurrentChargePeriodTitle>{`${t('charger_period')} ${period.index + 1}`}</CurrentChargePeriodTitle>
                    <CurrentChargePeriodTime>{`${new Date(period.p_start * 1000).toLocaleTimeString('hu').slice(0, -3)} - ${new Date(period.p_end * 1000).toLocaleTimeString('hu').slice(0, -3)}`}</CurrentChargePeriodTime>
                    <div style={{ width: '100%', backgroundColor: '#E2E8F0', height: '1px', marginTop: '16px', marginBottom: '16px' }} />
                    <CurrentChargeChargeDetailContainer>
                      <Icon path='chargers.energy' width="24px" height="24px" color="warning" />
                      <CurrentChargeChargeDetailLabel>{t('charger_energy')}</CurrentChargeChargeDetailLabel>
                      <CurrentChargeChargeDetailValue>{`${period.p_energy} kWh`}</CurrentChargeChargeDetailValue>
                    </CurrentChargeChargeDetailContainer>
                    <WhiteSpace height="16px" />
                    <CurrentChargeChargeDetailContainer>
                      <Icon path='chargers.time' width="24px" height="24px" color="primary" />
                      <CurrentChargeChargeDetailLabel>{t('charger_chargeTime')}</CurrentChargeChargeDetailLabel>
                      <CurrentChargeChargeDetailValue>{secondsToHumanReadable(period.p_end - period.p_start)}</CurrentChargeChargeDetailValue>
                    </CurrentChargeChargeDetailContainer>
                  </CurrentChargePeriodContainer>
                </>
              ))}
              <WhiteSpace height="16px" />
            </div>
          </ModalContainer>
        </Modal>
      )}
      <PageColumn>
      <WhiteSpace height={'12px'} />
      <PageTitle label={t('charger_generalInformation')} />
      <WhiteSpace height={'12px'} />

      <HeaderRow>
        <GeneralContainer>
          <GeneralCardRow>

            {/* OFFLINE */}
            {!charger.online && (
              <StatusContainer $bgColorTop="#E3E9F1" $bgColorBottom="#E3E9F1">
                <StatusTitle $bgColor="#E3E9F1" $fontColor="#7E8299">OFFLINE</StatusTitle>
                <StatusOfflineLabel>{dateToTimeAgoString(new Date(charger.lastPresence.replace(/\./g, '-')))}</StatusOfflineLabel>
                <WhiteSpace height="20px" />
                <Icon path="chargers.chargerStatusOffline" width="max(7vw, 89.6px)" height="max(7vw, 89.6px)" marginleft="calc((100% - max(7vw, 89.6px)) / 2)" />
              </StatusContainer>
            )}

            {/* LOADING */}
            {charger.online && !evseState && (
              <StatusContainer $bgColorTop="#E3E9F1" $bgColorBottom="#E3E9F1">
                <div style={{ width: "100%", height: "100%", textAlign: "center" }}>
                  <WhiteSpace height="40%" />
                  <span style={{ fontWeight: "700", fontSize: "24px" }}>
                    <LoaderDots />
                  </span>
                </div>
              </StatusContainer>
            )}

            {/* NOT CONNECTED */}
            {charger.online && evseState?.evse_state == 1 && (
              <StatusContainer $bgColorTop="#57D9BA" $bgColorBottom="#09BCA6">
                <StatusTitle $bgColor="#57D9BA" $fontColor="#FFFFFF">ONLINE</StatusTitle>
                <WhiteSpace height="2vmax" />
                <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center" }}>
                  <Icon path="chargers.unplugged" width="16px" height="16px" />
                  <WhiteSpace width="8px" />
                  <StatusLabel $color="#4F8378">{t('charger_carUnplugged')}</StatusLabel>
                </div>
                <WhiteSpace height="1vmax" />
                <Icon path="chargers.chargerStatusOnline" width="max(7vw, 89.6px)" height="max(7vw, 89.6px)" marginleft="calc((100% - max(7vw, 89.6px)) / 2)" />
              </StatusContainer>
            )}

            {/* CONNECTED - NOT AVAILABLE TO CHARGE (OR SO I WAS THINKING, BUT IT TURNS OUT IT COULD ALSO START A CHARGE?) */}
            {charger.online && evseState?.evse_state == 2 && (
              <StatusContainer $bgColorTop="#57D9BA" $bgColorBottom="#09BCA6">
                <StatusTitle $bgColor="#57D9BA" $fontColor="#FFFFFF">ONLINE</StatusTitle>
                <WhiteSpace height="1.5vmax" />
                <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center" }}>
                  <Icon path="chargers.pluggedIn" width="16px" height="16px" />
                  <WhiteSpace width="8px" />
                  <StatusLabel $color="#181C32">{t('charger_carPluggedIn')}</StatusLabel>
                </div>
                <WhiteSpace height=".5vmax" />
                {currentChargeDetails && currentChargeDetails.idtag_name && currentChargeDetails.idtag_name != '' ? <>
                  <Icon path="chargers.chargerStatusOnline" width="max(4.5vw, 57.6px)" height="max(4.5vw, 57.6px)" marginleft="calc((100% - max(4.5vw, 57.6px)) / 2)" />
                  <WhiteSpace height=".4vmax" />
                  <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center" }}>
                    <Icon path="chargers.rfidTag" width="18px" height="18px" color="white" />
                    <WhiteSpace width="4px" />
                    <StatusLabel $color="white">{currentChargeDetails.idtag_name || '-'}</StatusLabel>
                  </div>
                </> : <>
                  <Icon path="chargers.chargerStatusOnline" width="max(5.5vw, 70.4px)" height="max(5.5vw, 70.4px)" marginleft="calc((100% - max(5.5vw, 70.4px)) / 2)" />
                </>}
                <WhiteSpace height=".25vmax" />
                <div onClick={() => setIsShowingCurrentChargeDetails(true)} style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <StatusLabel $color="white">{t('charger_chargingInfo')}</StatusLabel>
                  <WhiteSpace width="2px" />
                  <Icon path="general.forwardArrow" width="16px" height="16px" color="white" />
                </div>


                {/* Added this part so user can start a charge in this mode too */}
                <div style={{ display: 'flex', position: "absolute", width: "100%", bottom: "3%" }}>
                  <WhiteSpace width='3%' />
                  <SlideToConfirm width='94%' label="Slide to START charging" onConfirm={startCharging} icon="chargers.power" iconColor="primary" bgColorLeft="#2A6FF4" bgColorRight="#56F2DF" />
                </div>


              </StatusContainer>
            )}

            {/* CONNECTED - AVAILABLE TO CHARGE */}
            {charger.online && (evseState?.evse_state == 3 || evseState?.evse_state == 4) && !evseState.chg_on && (
              <StatusContainer $bgColorTop="#57D9BA" $bgColorBottom="#09BCA6">
                <StatusTitle $bgColor="#57D9BA" $fontColor="#FFFFFF">ONLINE</StatusTitle>
                <WhiteSpace height="1.5vmax" />
                <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center" }}>
                  <Icon path="chargers.pluggedIn" width="16px" height="16px" />
                  <WhiteSpace width="8px" />
                  <StatusLabel $color="#181C32">{t('charger_carPluggedIn')}</StatusLabel>
                </div>
                <WhiteSpace height=".5vmax" />
                {currentChargeDetails && currentChargeDetails.idtag_name && currentChargeDetails.idtag_name != '' ? <>
                  <Icon path="chargers.chargerStatusOnline" width="max(4.5vw, 57.6px)" height="max(4.5vw, 57.6px)" marginleft="calc((100% - max(4.5vw, 57.6px)) / 2)" />
                  <WhiteSpace height=".4vmax" />
                  <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center" }}>
                    <Icon path="chargers.rfidTag" width="18px" height="18px" color="white" />
                    <WhiteSpace width="4px" />
                    <StatusLabel $color="white">{currentChargeDetails.idtag_name || '-'}</StatusLabel>
                  </div>
                </> : <>
                  <Icon path="chargers.chargerStatusOnline" width="max(5.5vw, 70.4px)" height="max(5.5vw, 70.4px)" marginleft="calc((100% - max(5.5vw, 70.4px)) / 2)" />
                </>}
                <WhiteSpace height=".25vmax" />
                <div onClick={() => setIsShowingCurrentChargeDetails(true)} style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <StatusLabel $color="white">{t('charger_chargingInfo')}</StatusLabel>
                  <WhiteSpace width="2px" />
                  <Icon path="general.forwardArrow" width="16px" height="16px" color="white" />
                </div>
                <div style={{ display: 'flex', position: "absolute", width: "100%", bottom: "3%" }}>
                  <WhiteSpace width='3%' />
                  <SlideToConfirm width='94%' label="Slide to START charging" onConfirm={startCharging} icon="chargers.power" iconColor="primary" bgColorLeft="#2A6FF4" bgColorRight="#56F2DF" />
                </div>
              </StatusContainer>
            )}

            {/* CHARGING */}
            {charger.online && (evseState?.evse_state == 3 || evseState?.evse_state == 4) && evseState.chg_on && (
              <StatusContainer $bgColorTop="#2B71F5" $bgColorBottom="#50D7DF">
                <StatusTitle $bgColor="#2B71F5" $fontColor="#FFFFFF">{t('charger_CHARGING')}</StatusTitle>
                <WhiteSpace height="1.5vmax" />
                {currentChargeDetails && currentChargeDetails.idtag_name && currentChargeDetails.idtag_name != '' ? <>
                  <Icon path="chargers.chargerStatusCharging" width="max(5.5vw, 70.4px)" height="max(5.5vw, 70.4px)" marginleft="calc((100% - max(5.5vw, 70.4px)) / 2)" />
                  <WhiteSpace height=".5vmax" />
                  <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center" }}>
                    <Icon path="chargers.rfidTag" width="18px" height="18px" color="white" />
                    <WhiteSpace width="4px" />
                    <StatusLabel $color="white">{currentChargeDetails.idtag_name || '-'}</StatusLabel>
                  </div>
                </> : <>
                  <Icon path="chargers.chargerStatusCharging" width="max(7vw, 89.6px)" height="max(7vw, 89.6px)" marginleft="calc((100% - max(7vw, 89.6px)) / 2)" />
                </>}
                <WhiteSpace height=".25vmax" />
                <div onClick={() => setIsShowingCurrentChargeDetails(true)} style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <StatusLabel $color="white">{t('charger_chargingInfo')}</StatusLabel>
                  <WhiteSpace width="2px" />
                  <Icon path="general.forwardArrow" width="16px" height="16px" color="white" />
                </div>
                <div style={{ display: 'flex', position: "absolute", width: "100%", bottom: "3%" }}>
                  <WhiteSpace width='3%' />
                  <SlideToConfirm width='94%' label="Slide to STOP charging" onConfirm={stopCharging} icon="chargers.power" iconColor="primary" bgColorLeft="#2A6FF4" bgColorRight="#E2594B" />
                </div>
              </StatusContainer>
            )}

            {/* BLOCKED */}
            {charger.online && evseState?.evse_state == 18 && (
              <StatusContainer $bgColorTop="#505369" $bgColorBottom="#7F8291">
                <StatusTitle $bgColor="#181C32" $fontColor="#FFFFFF">BLOCKED</StatusTitle>
                <WhiteSpace height="2vmax" />
                <Icon path="chargers.chargerStatusBlocked" width="max(7vw, 89.6px)" height="max(7vw, 89.6px)" marginleft="calc((100% - max(7vw, 89.6px)) / 2)" />
                <WhiteSpace height="1vmax" />
                <div onClick={() => handleChargerUpdateBlocked(false)} style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <StatusLabel $color="#FFFFFF">Unblock</StatusLabel>
                  <WhiteSpace width="2px" />
                  <Icon path="general.forwardArrow" width="16px" height="16px" color="white" />
                </div>
              </StatusContainer>
            )}

            {/* ERROR */}
            {charger.online && evseState && evseState.evse_state != 1 && evseState.evse_state != 2 && evseState.evse_state != 3 && evseState.evse_state != 4 && evseState.evse_state != 18 && (
              <StatusContainer $bgColorTop="#EB5172" $bgColorBottom="#FD4E4E">
                <StatusTitle $bgColor="#EB5172" $fontColor="#FFFFFF">ERROR</StatusTitle>
                <WhiteSpace height="2vmax" />
                <Icon path="chargers.chargerStatusError" width="max(6vw, 76.8px)" height="max(6vw, 76.8px)" marginleft="calc((100% - max(6vw, 76.8px)) / 2)" />
                <WhiteSpace height=".8vmax" />
                <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center" }}>
                  <StatusLabel $color="#FFFFFF">{`Code: ${evseState.evse_state.toString(16).toUpperCase().padStart(2, "0")} ${evseStateErrorTranslationTable[evseState.evse_state] ? `(${evseStateErrorTranslationTable[evseState.evse_state]})` : ''}`}</StatusLabel>
                </div>
                <WhiteSpace height=".8vmax" />
                <Navigator to={`/chargers/${charger.chargerId}/logs`}>
                  <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                    <StatusLabel $color="#FFFFFF">Check LOGs</StatusLabel>
                    <WhiteSpace width="2px" />
                    <Icon path="general.forwardArrow" width="16px" height="16px" color="white" />
                  </div>
                </Navigator>
              </StatusContainer>
            )}

            <GeneralCardBody>
              <IdentityRow>
                <IdentityBlock>
                  <ChargerNameLabel>
                    {t('charger_chargerName')}
                    <a title="Edit name">
                      <Icon path="general.edit" width="12px" height="12px" marginleft="10px" color="gray_600" onClick={() => setIsShowingEditChargerNameModal(true)} />
                    </a>
                    <a title="Copy name">
                      <Icon path="general.copy" width="12px" height="12px" onClick={() => { navigator.clipboard.writeText(charger.localName); addNotification('Copied', 'TEXT', 'normal', 1000) }} style={{ opacity: .4, marginLeft: '10px', cursor: 'pointer' }} />
                    </a>
                    {isAdmin &&
                      <>
                        <a title="Sync name (Local name becomes same as Firebase name)">
                          <Icon path="general.refresh" width="12px" height="12px" marginleft="10px" marginright="10px" color="gray_600" onClick={handleSyncChargerNameToFirebaseName} />
                        </a>
                        <a title="Sync name (Firebase name becomes same as Local name)">
                          <Icon path="general.refresh" width="12px" height="12px" color="gray_600" style={{transform: "scaleX(-1)", cursor: "pointer"}} onClick={handleSyncFirebaseNameToChargerName} />
                        </a>
                      </>}
                  </ChargerNameLabel>
                  <ChargerNameValue onClick={() => setIsShowingEditChargerNameModal(true)}>{charger.localName}</ChargerNameValue>
                </IdentityBlock>
                {/* Only show the user firebase node button to admins */}
                {isAdmin && (
                  <IdentityBlock>
                  <UserIdEmailLabel>{t('charger_userIdEmail')}
                    <a title="Copy userId">
                      <Icon path="general.copy" width="12px" height="12px" onClick={() => { navigator.clipboard.writeText(charger.userId); addNotification('Copied', 'TEXT', 'normal', 1000) }} style={{ opacity: .8, marginLeft: '10px', cursor: 'pointer' }} />
                    </a>
                  </UserIdEmailLabel>
                  <UserIdEmailValue>
                    {(charger.userId === "UNKNOWN" && isAdmin) ? <>
                      <ButtonWithIcon icon="general.plus" backgroundColor="dark" iconColor="white" label={t('charger_assignToUser')} onClick={() => { setIsAssigningChargerToUser(true); setAssigningChargerToUserInputValue(""); setAssigningChargerToUserError(null) }} />
                    </> : <>
                      <Navigator newTab to={`https://console.firebase.google.com/project/voltie-charger/database/voltie-charger-default-rtdb/data/~2Fvoltie~2Fchargerusers~2F${charger.userId}`}>
                        {charger.userId}<Icon path="general.forwardArrow" width="16px" height="16px" marginleft="5px" />
                      </Navigator>
                      <br />{charger.userEmail}
                    </>}
                  </UserIdEmailValue>
                  </IdentityBlock>
                )}
              </IdentityRow>
              <GeneralCardSpacer />
              <GeneralDetailsGrid>
                {/* Only show charger group to admins */}
                {isAdmin && (
                  <div>
                    <GeneralDetailLabel>{t('charger_group')}</GeneralDetailLabel>
                    <GeneralDetailValue>{chargerGroup || '-'}</GeneralDetailValue>
                  </div>
                )}
                <div>
                  <div style={{ display: 'flex' }}>
                    <GeneralDetailLabel>{t('charger_chargerId')}</GeneralDetailLabel>
                    <Icon path={"general.copy"} width="12px" height="12px" style={{ opacity: .4, marginLeft: '10px', cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText(charger.chargerId); addNotification('Copied', 'TEXT', 'normal', 1000) }} />
                  </div>
                  <GeneralDetailValue>{charger.chargerId}</GeneralDetailValue>
                </div>
                {/* Only show registation date to admins */}
                {isAdmin && (
                  <div>
                    <GeneralDetailLabel>{t('charger_registrationDate')}</GeneralDetailLabel>
                    <GeneralDetailValue>{charger.registrationDate}</GeneralDetailValue>
                  </div>
                )}
                <div>
                  <div style={{ display: 'flex' }}>
                    <GeneralDetailLabel>{t('charger_serialNumber')}</GeneralDetailLabel>
                    <Icon path={"general.copy"} width="12px" height="12px" style={{ opacity: .4, marginLeft: '10px', cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText(charger.serialNumber == ' ' ? '-' : charger.serialNumber || '-'); addNotification('Copied', 'TEXT', 'normal', 1000) }} />
                  </div>
                  <GeneralDetailValue>{charger.serialNumber == ' ' ? '-' : charger.serialNumber || '-'}</GeneralDetailValue>
                </div>
                <div onClick={openKwhPriceModal} style={{ cursor: 'pointer' }}>
                  <div style={{ display: 'flex' }}>
                    <GeneralDetailLabel>{t('charger_kwhPrice')}</GeneralDetailLabel>
                    <Icon path="general.edit" width="10px" height="10px" margintop="3px" marginleft="6px" color="gray_600" />
                  </div>
                  <GeneralDetailValue>{shownKwhPrice ? `${formatPrice(Number(shownKwhPrice.value), shownKwhPrice.currency, i18n.language)} ${shownKwhPrice.currency}` : '-'}</GeneralDetailValue>
                </div>
                <div onClick={() => setIsShowingStatisticsEmailModal(true)} style={{ cursor: 'pointer' }}>
                  <div style={{ display: 'flex' }}>
                    <GeneralDetailLabel>{t('charger_statisticsEmail')}</GeneralDetailLabel>
                    <Icon path="general.edit" width="10px" height="10px" margintop="3px" marginleft="6px" color="gray_600" />
                  </div>
                  <GeneralDetailValue>{statisticsEmailEnabled ? `${t('charger_enabled')} · ${1 + statisticsEmails.filter(e => e.toLowerCase() !== (charger.userEmail || '').toLowerCase()).length}` : t('charger_disabled')}</GeneralDetailValue>
                </div>
              </GeneralDetailsGrid>
              <GeneralDetailsDivider />
              {evseState && chargerDetails && (
                <SwitchRow>
                <SwitchItem style={chargerDetails.rfidAccessMode === 0 && (evseState.autostart === 1 || evseState.autostart === 0) ? {} : { pointerEvents: 'none', opacity: 0.5 }}>
                  <GeneralDetailValue style={{ marginTop: '4px' }}>{t('charger_autostart')}</GeneralDetailValue>
                  <WhiteSpace width="12px" />
                  <Switch toggled={evseState.autostart === 1} setToggled={(value) => { setEvseState({ ...evseState, autostart: value ? 1 : 0 }); handleUpdateAutostart(value) }} />
                </SwitchItem>
                <SwitchItem style={(chargerDetails.rfidAvailable && (chargerDetails.rfidAccessMode === 1 || chargerDetails.rfidAccessMode === 0)) ? {} : { pointerEvents: 'none', opacity: 0.5 }}>
                  <GeneralDetailValue style={{ marginTop: '4px' }}>{t('charger_rfid')}</GeneralDetailValue>
                  <WhiteSpace width="12px" />
                  <Switch toggled={chargerDetails.rfidAvailable && chargerDetails.rfidAccessMode === 1} setToggled={(value) => {
                    setChargerDetails({ ...chargerDetails, rfidAccessMode: value ? 1 : 0 })
                    handleUpdateRFIDReader(value)

                    // If RFID is on, autocharge is going to turn off, so we might aswell turn it off now
                    if (value == true) {
                      setEvseState({ ...evseState, autostart: 0 })
                    } else {
                      setEvseState({ ...evseState, autostart: 1 })
                    }
                  }} />
                </SwitchItem>
                <SwitchItem>
                  <GeneralDetailValue style={{ marginTop: '4px' }}>{t('charger_available')}</GeneralDetailValue>
                  <WhiteSpace width="12px" />
                  <Switch toggled={evseState.evse_state != 18} setToggled={(value) => { handleChargerUpdateBlocked(!value) }} />
                </SwitchItem>
                </SwitchRow>
              )}
            </GeneralCardBody>
          </GeneralCardRow>
        </GeneralContainer>
        <StatCardsColumn>
          <StatCard $color="dark">
            <StatCardIconContainer>
              <Icon path="chargers.power" width="100%" height="100%" />
            </StatCardIconContainer>
            <StatCardBody>
              <StatCardTitle $white>{t('charger_allCharges')}</StatCardTitle>
              <StatCardValue $white>{charges ? charges.length : <LoaderDots />}<StatCardUnit>{t('charger_pcs')}</StatCardUnit></StatCardValue>
            </StatCardBody>
          </StatCard>
          <StatCard $color="warning">
            <StatCardIconContainer>
              <Icon path="chargers.energy" width="100%" height="100%" color="warning" />
            </StatCardIconContainer>
            <StatCardBody>
              <StatCardTitle>{t('charger_allChargedEnergy')}</StatCardTitle>
              <StatCardValue>{charger.online ? chargerDetails ? (chargerDetails.lifetimeWh / 1000).toFixed(2) : <LoaderDots /> : '-'}<StatCardUnit>kWh</StatCardUnit></StatCardValue>
            </StatCardBody>
          </StatCard>
          <StatCard $color="primary">
            <StatCardIconContainer>
              <Icon path="chargers.time" width="100%" height="100%" color="primary" />
            </StatCardIconContainer>
            <StatCardBody>
              <StatCardTitle>{t('charger_allChargetime')}</StatCardTitle>
              <StatCardValue>{charger.online ? chargerDetails ? secondsToHumanReadable(chargerDetails.lifetimeChgSec) : <LoaderDots /> : '-h'}</StatCardValue>
            </StatCardBody>
          </StatCard>
          <StatCard $color="dark">
            <StatCardIconContainer>
              <Icon path="chargers.power" width="100%" height="100%" />
            </StatCardIconContainer>
            <StatCardBody>
              <StatCardTitle $white>{t('charger_relaySwitches')}</StatCardTitle>
              <StatCardValue $white>{charger.online ? chargerDetails ? chargerDetails.lifetimeRealySwitches : <LoaderDots /> : '-'}<StatCardUnit>{t('charger_pcs')}</StatCardUnit></StatCardValue>
            </StatCardBody>
          </StatCard>
        </StatCardsColumn>
      </HeaderRow>
      <WhiteSpace height="18px" />
      <LowerScroller>
        <ConnectionContainer>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ConnectionTitle>{t('charger_connection')}</ConnectionTitle>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['ethernet', 'wifi', 'gsm'].map(conn => (
                <ConnectionTab key={conn} $selected={selectedConnection == conn} onClick={() => setSelectedConnection(conn as 'wifi' | 'ethernet' | 'gsm')}>
                  {conn == 'wifi' ? t('charger_wifi') : conn == 'ethernet' ? t('charger_ethernet') : t('charger_cellular')}
                </ConnectionTab>
              ))}
            </div>
          </div>
          <ConnectionTableDivider $left="31%" />
          <ConnectionTableDivider $left="64%" />
          <table style={{ width: '100%' }}>
            <tbody>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_ipAddress')}</ConnectionLabel>
                  <ConnectionValue>{charger.online ? chargerDetails ? (selectedConnection == 'gsm' ? '-' : chargerDetails.connectionData[selectedConnection]?.ip || '-') : <LoaderDots /> : '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_dlmState')}</ConnectionLabel>
                  <ConnectionValue>{charger.online ? chargerDetails ? chargerDlmAndMidStateCodeToMeaning(chargerDetails.dlmState) : <LoaderDots /> : '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_software')}</ConnectionLabel>
                  <ConnectionValue>{charger.swVersion}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_macAddress')}</ConnectionLabel>
                  <ConnectionValue>{charger.online ? chargerDetails ? (selectedConnection == 'gsm' ? '-' : chargerDetails.connectionData[selectedConnection]?.mac || '-') : <LoaderDots /> : '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_midMeterState')}</ConnectionLabel>
                  <ConnectionValue>{charger.online ? chargerDetails ? chargerDlmAndMidStateCodeToMeaning(chargerDetails.midMeterState) : <LoaderDots /> : '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_firmware')}</ConnectionLabel>
                  <ConnectionValue>{charger.fwVersion}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_serverConnection')}</ConnectionLabel>
                  <ConnectionValue>{charger.lastPresence}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell></ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_operatingSystem')}</ConnectionLabel>
                  <ConnectionValue>{charger.osVersion}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
            </tbody>
          </table>
        </ConnectionContainer>
        {isAdmin && <>
        <WhiteSpace height="18px" />
        <ConnectionContainer>
          <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setIsShowingDeviceInformation(!isShowingDeviceInformation)}>
            <ConnectionTitle>{t('charger_deviceInformation')}</ConnectionTitle>
            <div style={{ marginLeft: 'auto', paddingRight: '12px' }}>
              <Icon path={isShowingDeviceInformation ? "general.upArrow" : "general.downArrow"} width="22px" height="22px" color="gray_600" />
            </div>
          </div>
          {isShowingDeviceInformation && <>
          <ConnectionTableDivider $left="31%" />
          <ConnectionTableDivider $left="64%" />
          <table style={{ width: '100%' }}>
            <tbody>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_platform')}</ConnectionLabel>
                  <ConnectionValue>{charger.platform || '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_storage')}</ConnectionLabel>
                  <ConnectionValue>{charger.storage ? `${charger.storage}${charger.storageSizeGb ? ` ${charger.storageSizeGb} GB` : ''}` : '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_uptime')}</ConnectionLabel>
                  <ConnectionValue>{charger.uptime ? `${Math.floor(charger.uptime / 86400)}d ${Math.floor((charger.uptime % 86400) / 3600)}h` : '-'}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_midMeter')}</ConnectionLabel>
                  <ConnectionValue>{charger.midMeterEn ? (charger.midMeter || '-') : t('charger_disabled')}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_dlmMeter')}</ConnectionLabel>
                  <ConnectionValue>{charger.dlmMeter || '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_dlmMode')}</ConnectionLabel>
                  <ConnectionValue>{charger.dlmMode || '-'}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_gsmModem')}</ConnectionLabel>
                  <ConnectionValue>{charger.gsmModem || '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_gsmNetwork')}</ConnectionLabel>
                  <ConnectionValue>{charger.gsmNetwork || '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_gsmApn')}</ConnectionLabel>
                  <ConnectionValue>{charger.gsmApn || '-'}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_gsmMode')}</ConnectionLabel>
                  <ConnectionValue>{charger.gsmMode || '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_gsmIccid')}</ConnectionLabel>
                  <ConnectionValue>{charger.gsmIccid || '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_event')}</ConnectionLabel>
                  <ConnectionValue>{charger.event || '-'}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_ethernetMode')}</ConnectionLabel>
                  <ConnectionValue>{charger.ethMode || '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_ethernetIp')}</ConnectionLabel>
                  <ConnectionValue>{charger.ethIp || '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_wifiMode')}</ConnectionLabel>
                  <ConnectionValue>{charger.wifiMode || '-'}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_wifiSsid')}</ConnectionLabel>
                  <ConnectionValue>{charger.wifiSsid || '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_apModeSsid')}</ConnectionLabel>
                  <ConnectionValue>{charger.apModeSsid || '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_wifiIp')}</ConnectionLabel>
                  <ConnectionValue>{charger.wifiIp || '-'}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_midMeterEnergy')}</ConnectionLabel>
                  <ConnectionValue>{charger.midMeterEn ? (charger.midKwh != null && charger.midKwh >= 0 ? `${charger.midKwh} kWh` : '-') : t('charger_disabled')}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_accessMode')}</ConnectionLabel>
                  <ConnectionValue>{charger.accessMode ?? '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_powerSharing')}</ConnectionLabel>
                  <ConnectionValue>{charger.psEnabled ? t('charger_yes') : t('charger_no')}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_mcuVersion')}</ConnectionLabel>
                  <ConnectionValue>{charger.mcuVersion ?? '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_hpowVersion')}</ConnectionLabel>
                  <ConnectionValue>{charger.hpowVersion ?? '-'}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_dailyLogVersion')}</ConnectionLabel>
                  <ConnectionValue>{charger.dailyLogVersion ?? '-'}</ConnectionValue>
                </ConnectionTableCell>
              </tr>
              <tr style={{ display: 'flex' }}>
                <ConnectionTableCell>
                  <ConnectionLabel>{t('charger_outOfService')}</ConnectionLabel>
                  <ConnectionValue>{charger.outOfService ? t('charger_yes') : t('charger_no')}</ConnectionValue>
                </ConnectionTableCell>
                <ConnectionTableCell></ConnectionTableCell>
                <ConnectionTableCell></ConnectionTableCell>
              </tr>
            </tbody>
          </table>
          </>}
        </ConnectionContainer>
        </>}
        <WhiteSpace height="18px" />
        <DailyMeterReadings charger={charger} />
        {/* Only show comments to admins for now, firebase is not respoding to users */}
        {isAdmin && <>
          <WhiteSpace height="18px" />
          <CommentContainer>
            <CommentTitle>{t('charger_comments')}</CommentTitle>
            <WhiteSpace height="8px" />
            <div style={{ position: "absolute", top: '24px', right: '24px' }}>
              <ButtonWithIcon icon="general.plus" label={t('charger_newComment')} onClick={() => setIsShowingNewCommentModal(true)} backgroundColor="dark" iconColor="white" />
            </div>
            {chargerComments && Object.keys(chargerComments).sort().reverse().map((comment) => <React.Fragment key={comment}>
              <div style={{ display: 'flex' }}>
                <CommentAuthorText>{chargerComments[comment].author || '-'}</CommentAuthorText>
                <WhiteSpace width="4px" />
                <CommentDateText>•</CommentDateText>
                <WhiteSpace width="4px" />
                <CommentDateText>{`${new Date(parseInt(comment)).toLocaleDateString('hu').replace(/\. /g, '.') || '-'} ${new Date(parseInt(comment)).toLocaleTimeString('hu') || '-'}`}</CommentDateText>
              </div>
              <WhiteSpace height="8px" />
              <CommentMessageText>{chargerComments[comment].comment || '-'}</CommentMessageText>
              <WhiteSpace height="32px" />
            </React.Fragment>)}
          </CommentContainer>
        </>}
      </LowerScroller>
      </PageColumn>
    </>
  )
}